/**
 * LockToggle — "I won him", the mirror of the ⊘ gone mark.
 *
 * 🔴 ITS OWN COMPONENT, NOT A MODE ON StarToggle. The two controls look alike
 * and mean nothing alike: a star is a VIEW about a player that follows him into
 * every plan and every column, a lock is a FACT about one plan's roster. Giving
 * StarToggle a `kind` prop would put two glyph sets, two colours, two titles and
 * two aria labels behind one branch — and the first change to either would have
 * to reason about the other. They share a shape vocabulary (see the two variants
 * below, deliberately identical to StarToggle's) and nothing else.
 *
 * 🔴 AN INLINE SVG, NOT 🔒. The emoji padlock is a COLOUR glyph on every
 * platform that has it: it ignores `color`, so the locked and unlocked states
 * would render in the same yellow-brown regardless of the token below, which is
 * the one thing this control cannot afford. The SVG inherits currentColor.
 *
 * TWO VARIANTS, matching StarToggle exactly, because they sit on the same
 * surfaces:
 *   boxed   — a bordered square in a row of other controls.
 *   overlay — a filled circle ON a player's headshot, carrying its own opaque
 *             disc so it stays legible over any jersey.
 */

import React from "react";
import { theme } from "../data/theme";

/**
 * The shackle is the state, and it is the ONLY thing that moves between them.
 * Locked: closed, centred over the body. Unlocked: open, hinged left and lifted
 * — the standard reading, and one that survives being 10px wide on a headshot
 * where a colour difference alone would not.
 */
function LockGlyph({ locked, size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d={locked ? "M5 7V5a3 3 0 0 1 6 0v2" : "M5 7V5a3 3 0 0 1 5.9-.7"}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <rect
        x="3.5" y="7" width="9" height="6.5" rx="1.4"
        stroke="currentColor"
        strokeWidth="1.4"
        fill={locked ? "currentColor" : "none"}
        fillOpacity={locked ? 0.28 : 0}
      />
    </svg>
  );
}

export default function LockToggle({
  locked = false,
  onToggle,
  name = "",
  price = null,
  size = 20,
  variant = "boxed",
  // Same rule as StarToggle: every surface names its own instance, because
  // more than one of these can be on screen at once.
  testId = "lock-toggle",
  style,
}) {
  const overlay = variant === "overlay";
  const who = name ? ` ${name}` : "";
  const paid = price == null ? "" : ` at $${Math.round(price)}`;
  const glyph = Math.max(10, Math.round(size * 0.7));

  const base = {
    width: size, height: size, flexShrink: 0, padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    lineHeight: 1,
    cursor: "pointer",
  };
  const chrome = overlay
    ? {
        borderRadius: "50%",
        // Opaque in BOTH states, for the reason StarToggle's is: a translucent
        // disc tints with whatever headshot is under it, so the same control
        // would look different on every card in the row.
        background: locked ? theme.colors.bg.deep : "rgba(10,15,26,0.78)",
        border: `1px solid ${locked ? theme.colors.lock : "rgba(255,255,255,0.22)"}`,
        color: locked ? theme.colors.lock : theme.colors.text.muted,
      }
    : {
        borderRadius: theme.radius.sm,
        background: locked ? theme.helpers.lockBg() : "transparent",
        border: `1px solid ${locked ? theme.colors.lock : theme.colors.border.default}`,
        color: locked ? theme.colors.lock : theme.colors.text.faint,
      };

  return (
    <button
      type="button"
      aria-pressed={locked}
      data-testid={testId}
      title={locked
        ? `Unlock${who} — he is not yours after all`
        : `Lock${who}${paid} — I won him`}
      onClick={(e) => {
        // The empty branch of a target card has a root onClick that selects the
        // cell, and this control sits inside a clickable list elsewhere. A lock
        // must never also do the surrounding thing.
        e.stopPropagation();
        if (onToggle) onToggle();
      }}
      style={{ ...base, ...chrome, ...style }}
    ><LockGlyph locked={locked} size={glyph} /></button>
  );
}
